/* eslint-disable */
import saveStoreDraft from "../../../utils/saveStoreDraft";
import useStoreFormDraft from "../../../hooks/useStoreFormDraft";
import { useTranslation as useUiTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Form from "react-bootstrap/Form";
import { applyStoreDraft } from "../../../redux/store/store";
import { doc } from "firebase/firestore";
import { db } from "../../../firebase";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axios from "axios";
import {
  Check,
  RefreshCw,
  Info,
  FileSpreadsheet,
  ShieldAlert,
  Database,
  Settings,
  X,
} from "lucide-react";
import apicache from "../../clearCach/clear";

export default function ActivationGooglesheetV2({ close }) {
  const { t: uiT } = useUiTranslation();
  const store = useSelector((state) => state.StoreReducer);
  const dispatch = useDispatch();
  const MySwal = withReactContent(Swal);
  const [loading, setLoading] = useState(false);
  const products = useSelector((state) => state.ProductsReducer);
  const account = useSelector((state) => state.AccountReducer);

  const showAlert = (icon, title, text) => {
    return Swal.fire({
      icon,
      title,
      text,
      confirmButtonText: uiT("ship.ok"),
      confirmButtonColor: "rgb(141, 93, 167)",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: true,
      backdrop: `
        rgba(0,0,0,0.7)
      `,
      position: "center",
      customClass: {
        popup: "swal-popup-on-top",
        backdrop: "swal-backdrop-on-top",
        container: "swal-container-on-top",
      },
      willOpen: () => {
        // Ensure this alert appears on top of everything
        document.querySelector(".swal2-container").style.zIndex = "9999999999";
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if the URL is being cleared (service deactivation)
    const isDeactivating = !data.googleSheet || data.googleSheet.trim() === "";

    // If not deactivating, validate the form inputs
    if (!isDeactivating) {
      // Validate URL format
      if (
        !(
          data.googleSheet.startsWith("https://script.google.com/macros/") &&
          data.googleSheet.endsWith("/exec")
        )
      ) {
        await showAlert(
          "warning",
          "صيغة العنوان غير صحيحة",
          'يجب أن يبدأ بعنوان "https://script.google.com/macros/" وينتهي بـ "/exec"'
        );
        return;
      }

      // Check if a language is selected
      if (!data.frenchs && !data.arabics && !data.numberS) {
        await showAlert(
          "warning",
          "يرجى اختيار لغة",
          "يجب اختيار لغة لتلقي الولايات والبلديات"
        );
        return;
      }
    }

    apicache(account, products);
    setLoading(true);
    try {
      // If deactivating, clear all related service settings
      if (isDeactivating) {
        const updatedData = {
          ...data,
          googleSheet: "",
          frenchs: false,
          arabics: false,
          numberS: false,
        };

        await saveStoreDraft(doc(db, "store", data.id), store, updatedData);
        dispatch(applyStoreDraft(store, updatedData));
        setData(updatedData);
        setLoading(false);
        await showAlert("success", "تم إلغاء تفعيل الخدمة بنجاح", "");
      } else {
        // Normal activation flow
        await saveStoreDraft(doc(db, "store", data.id), store, data);
        dispatch(applyStoreDraft(store, data));
        setLoading(false);
        await showAlert("success", "تم تفعيل التطبيق", "");
        close();
      }
    } catch (error) {
      setLoading(false);
      await showAlert(
        "error",
        isDeactivating
          ? "حدث خطأ أثناء إلغاء تفعيل الخدمة"
          : "حدث خطأ أثناء تفعيل التطبيق",
        "يرجى المحاولة مرة أخرى"
      );
      console.error("Error saving Google Sheet settings:", error);
    }
  };

  const [data, setData] = useStoreFormDraft(store);

  const handleFrench = () => {
    setData({ ...data, frenchs: true, arabics: false, numberS: false });
  };

  const handleArabic = () => {
    setData({ ...data, frenchs: false, arabics: true, numberS: false });
  };

  const handleNumber = () => {
    setData({ ...data, frenchs: false, arabics: false, numberS: true });
  };

  const clearGoogleSheet = () => {
    setData({ ...data, googleSheet: "" });
  };

  // Add this style to ensure SweetAlert appears on top
  useEffect(() => {
    // Add CSS to make sure SweetAlert appears on top of other modals
    const style = document.createElement("style");
    style.innerHTML = `
      .swal-container-on-top {
        z-index: 9999999999 !important;
      }
      .swal-backdrop-on-top {
        background: rgba(0,0,0,0.7) !important;
      }
      .swal-popup-on-top {
        position: relative;
        box-shadow: 0 0 20px rgba(0,0,0,0.2);
      }
      body.swal2-shown {
        overflow: hidden;
      }
      body.swal2-shown > [aria-hidden="true"] {
        filter: blur(3px);
      }
    `;
    document.head.appendChild(style);

    // Add a click handler to ensure modal shows on top
    const handleDocumentClick = () => {
      const swalContainer = document.querySelector(".swal2-container");
      if (swalContainer) {
        swalContainer.style.zIndex = "9999999999";
      }
    };

    document.addEventListener("click", handleDocumentClick);

    return () => {
      document.head.removeChild(style);
      document.removeEventListener("click", handleDocumentClick);
    };
  }, []);

  // Check if service is currently active
  const isServiceActive = data.googleSheet && data.googleSheet.trim() !== "";

  return (
    <div className="min-h-[400px] h-fit p-6" dir="rtl">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-[#9e68ba] mb-2">
          {uiT("servicePageActivation.googleSheet.headerTitle")}
        </h1>
        <p className="text-gray-600">
          {uiT("servicePageActivation.googleSheet.headerDescription")}
        </p>
      </div>

      {/* Service Status Indicator */}
      <div
        className={`mb-4 p-3 rounded-lg ${
          isServiceActive
            ? "bg-green-50 border border-green-200"
            : "bg-gray-50 border border-gray-200"
        }`}
      >
        <div className="flex gap-x-2 items-center">
          <div
            className={`w-3 h-3 rounded-full mr-2 ${
              isServiceActive ? "bg-green-500" : "bg-gray-400"
            }`}
          ></div>
          <span
            className={`text-sm font-medium ${
              isServiceActive ? "text-green-700" : "text-gray-700"
            }`}
          >
            {isServiceActive ? uiT("servicePageActivation.googleSheet.statusActive") : uiT("servicePageActivation.orderdz.notActive.title")}
          </span>
        </div>
      </div>

      {/* Activation Form */}
      <div className="bg-white rounded-xl shadow-sm border border-[#fbf4ff] p-6 mb-6">
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#9e68ba] mb-4">
              {isServiceActive
                ? uiT("servicePageActivation.googleSheet.formTitleActive")
                : uiT("servicePageActivation.googleSheet.formTitleInactive")}
            </h3>

            {/* App Script URL Input */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {uiT("servicePageActivation.googleSheet.inputLabel")}{" "}
                {!isServiceActive && <span className="text-red-500">*</span>}
              </label>
              <div className="flex">
                <input
                  type="text"
                  placeholder={uiT("ui.scriptExample")}
                  value={data.googleSheet || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setData({ ...data, googleSheet: value });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg rounded-r-none focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500"
                  style={{ direction: "ltr", textAlign: "left" }}
                />
                <button
                  type="button"
                  onClick={clearGoogleSheet}
                  className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg rounded-l-none border border-gray-300 border-l-0"
                >
                  <X size={18} />
                </button>
              </div>
              {data.googleSheet?.length > 0 &&
                !(
                  data.googleSheet.startsWith(
                    "https://script.google.com/macros/"
                  ) && data.googleSheet.endsWith("/exec")
                ) && (
                  <p className="text-red-500 text-sm mt-1">
                    {uiT("servicePageActivation.googleSheet.inputValidation")}
                  </p>
                )}
              {!isServiceActive && (
                <p className="text-sm text-gray-500 mt-1">
                  {uiT("servicePageActivation.googleSheet.inputNote")}
                </p>
              )}
            </div>

            {/* Language Selection - Only show if service is active or being activated */}
            {(isServiceActive || data.googleSheet) && (
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {uiT("servicePageActivation.googleSheet.languageLabel")}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-4">
                  <div
                    className={`flex items-center px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                      data.frenchs
                        ? "bg-[#faf1ff] border-purple-300"
                        : "bg-white border-gray-300 hover:bg-gray-50"
                    }`}
                    onClick={handleFrench}
                  >
                    <input
                      type="checkbox"
                      checked={data.frenchs}
                      onChange={handleFrench}
                      className="h-4 w-4 text-[#9e68ba] rounded border-gray-300 focus:ring-purple-200 ml-2"
                    />
                    <span className="text-gray-700">{uiT("login.language.fr")}</span>
                  </div>

                  <div
                    className={`flex items-center px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                      data.arabics
                        ? "bg-[#faf1ff] border-purple-300"
                        : "bg-white border-gray-300 hover:bg-gray-50"
                    }`}
                    onClick={handleArabic}
                  >
                    <input
                      type="checkbox"
                      checked={data.arabics}
                      onChange={handleArabic}
                      className="h-4 w-4 text-[#9e68ba] rounded border-gray-300 focus:ring-purple-200 ml-2"
                    />
                    <span className="text-gray-700">{uiT("login.language.ar")}</span>
                  </div>

                  <div
                    className={`flex items-center px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                      data.numberS
                        ? "bg-[#faf1ff] border-purple-300"
                        : "bg-white border-gray-300 hover:bg-gray-50"
                    }`}
                    onClick={handleNumber}
                  >
                    <input
                      type="checkbox"
                      checked={data.numberS}
                      onChange={handleNumber}
                      className="h-4 w-4 text-[#9e68ba] rounded border-gray-300 focus:ring-purple-200 ml-2"
                    />
                    <span className="text-gray-700">
                      {uiT("servicePageActivation.googleSheet.languageNumbers")}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Tips Section */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-100 mb-6">
              <h4 className="font-medium text-lg text-green-800 mb-2 flex items-center">
                <Info size={18} className="ml-2" />
                {uiT("servicePageActivation.googleSheet.tipsTitle")}
              </h4>
              <ul className="text-sm text-gray-700 space-y-2 mr-5 list-disc">
                <li className="flex items-start">
                  <FileSpreadsheet
                    size={16}
                    className="text-green-500 ml-2 mt-1 flex-shrink-0"
                  />
                  <span>
                    {uiT("servicePageActivation.googleSheet.tips.customTable")}
                  </span>
                </li>
                <li className="flex items-start">
                  <ShieldAlert
                    size={16}
                    className="text-green-500 ml-2 mt-1 flex-shrink-0"
                  />
                  <span>
                    {uiT("servicePageActivation.googleSheet.tips.permissions")}
                  </span>
                </li>
                <li className="flex items-start">
                  <Database
                    size={16}
                    className="text-green-500 ml-2 mt-1 flex-shrink-0"
                  />
                  <span>
                    {uiT("servicePageActivation.googleSheet.tips.formulas")}
                  </span>
                </li>
                <li className="flex items-start">
                  <Settings
                    size={16}
                    className="text-green-500 ml-2 mt-1 flex-shrink-0"
                  />
                  <span>
                    {uiT("servicePageActivation.googleSheet.tips.formatting")}
                  </span>
                </li>
              </ul>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-2.5 ${
                isServiceActive
                  ? "bg-[#9b59bf] hover:bg-[#814a9f]"
                  : data.googleSheet
                  ? "bg-[#9b59bf] hover:bg-[#814a9f]"
                  : "bg-red-500 hover:bg-red-600"
              } text-white font-medium rounded-lg transition-colors flex items-center ${
                loading ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {loading ? (
                <>
                  <RefreshCw className="animate-spin ml-2" size={18} />
                  {data.googleSheet ? uiT("navbar.try.saving") : uiT("servicePageActivation.upsell.button.disabling")}
                </>
              ) : (
                <>
                  <Check className="ml-2" size={18} />
                  {data.googleSheet ? uiT("adduser.save_settings") : uiT("services.order_exporting.deactivate_btn")}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
